import React, { useState } from 'react';
import { Brain, Volume2, VolumeX, Play, RotateCcw } from 'lucide-react';
import { sampleFlashCardSets, flashCardSettings } from '../data/flashCardsData';
import deepseekApi from '../services/deepseekApi';

const FlashCardsFallback = ({ onBack }) => {
  const [selectedSet, setSelectedSet] = useState(null);
  const [step, setStep] = useState('select'); // select, playing, results

  const handleSelectSet = (set) => {
    setSelectedSet(set);
    setStep('playing');
  };

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div
        className="relative rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(147, 51, 234, 0.9), rgba(219, 39, 119, 0.9)), url(https://images.unsplash.com/photo-1606092195730-5d7b9af1efc5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-8 h-8" />
            <h2 className="text-3xl font-bold">Flash Cards</h2>
          </div>
          <p className="text-purple-100 text-lg">
            Practice with our sample flash card sets (AI generation temporarily unavailable)
          </p>
        </div>
      </div>

      {step === 'select' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900">Choose a Flash Card Set</h3>
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Back to Templates
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sampleFlashCardSets.map(set => (
              <div key={set.id} className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{set.title}</h4>
                    <p className="text-gray-600 text-sm mb-3">{set.description}</p>
                  </div>
                  <span className="text-2xl">🧠</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                  <div>
                    <span className="text-gray-500">Cards:</span>
                    <span className="font-medium text-gray-900 ml-1">{set.totalCards}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Language:</span>
                    <span className="font-medium text-gray-900 ml-1">{set.language}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Difficulty:</span>
                    <span className="font-medium text-gray-900 ml-1 capitalize">{set.difficulty}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Category:</span>
                    <span className="font-medium text-gray-900 ml-1 capitalize">{set.category}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleSelectSet(set)}
                  className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Start Learning
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 'playing' && selectedSet && (
        <FlashCardsPlayer
          flashCards={selectedSet}
          onBack={() => setStep('select')}
          onComplete={() => setStep('results')}
        />
      )}

      {step === 'results' && selectedSet && (
        <ResultsSummary
          flashCards={selectedSet}
          onRestart={() => setStep('select')}
          onPlayAgain={() => setStep('playing')}
        />
      )}
    </div>
  );
};

const FlashCardsPlayer = ({ flashCards, onBack, onComplete }) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoAdvance, setAutoAdvance] = useState(false);

  const currentCard = flashCards.cards[currentCardIndex];
  const isLastCard = currentCardIndex === flashCards.cards.length - 1;
  const progress = ((currentCardIndex + 1) / flashCards.cards.length) * 100;

  const speakText = (text) => {
    if (soundEnabled) {
      deepseekApi.speakText(text, flashCards.language);
    }
  };

  const handleNext = () => {
    if (isLastCard) {
      onComplete();
    } else {
      setCurrentCardIndex(prev => prev + 1);
      setShowAnswer(false);
    }
  };

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(prev => prev - 1);
      setShowAnswer(false);
    }
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
    if (autoAdvance) {
      setTimeout(() => {
        handleNext();
      }, flashCardSettings.autoAdvanceDelay);
    }
  };

  React.useEffect(() => {
    if (currentCard && soundEnabled) {
      speakText(currentCard.question);
    }
  }, [currentCardIndex]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Back to Sets
            </button>
            <div className="text-sm text-gray-600">
              Card {currentCardIndex + 1} of {flashCards.cards.length}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded-lg transition-colors ${
                soundEnabled 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            
            <button
              onClick={() => setAutoAdvance(!autoAdvance)}
              className={`p-2 rounded-lg transition-colors ${
                autoAdvance 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              <Play className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Flash Card */}
      <FlashCard
        card={currentCard}
        showAnswer={showAnswer}
        onShowAnswer={handleShowAnswer}
        onSpeak={speakText}
        soundEnabled={soundEnabled}
      />

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePrevious}
          disabled={currentCardIndex === 0}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        
        {showAnswer && (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            {isLastCard ? 'Complete' : 'Next Card'}
          </button>
        )}
      </div>
    </div>
  );
};

const FlashCard = ({ card, showAnswer, onShowAnswer, onSpeak, soundEnabled }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
      {/* Image */}
      <div className="h-48 bg-gray-100 overflow-hidden">
        <img
          src={card.imageUrl}
          alt={card.imageAlt}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
          }}
        />
      </div>

      <div className="p-6">
        {/* Question */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-3">
            <h3 className="text-lg font-medium text-gray-900">Question</h3>
            {soundEnabled && (
              <button
                onClick={() => onSpeak(card.question)}
                className="p-1 text-purple-600 hover:text-purple-700 transition-colors"
              >
                <Volume2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="text-xl text-gray-800">{card.question}</p>
        </div>

        {/* Answer Section */}
        {!showAnswer ? (
          <div className="text-center">
            <button
              onClick={onShowAnswer}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              Show Answer
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-medium text-green-700">Answer</h4>
                {soundEnabled && (
                  <button
                    onClick={() => onSpeak(card.answer)}
                    className="p-1 text-green-600 hover:text-green-700 transition-colors"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-lg text-green-800 font-medium">{card.answer}</p>
            </div>
            
            {card.explanation && (
              <div>
                <h4 className="font-medium text-blue-700 mb-2">Explanation</h4>
                <p className="text-blue-800">{card.explanation}</p>
              </div>
            )}
            
            {card.keywords && card.keywords.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Keywords</h4>
                <div className="flex flex-wrap gap-2">
                  {card.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ResultsSummary = ({ flashCards, onRestart, onPlayAgain }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Brain className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Congratulations!
        </h3>
        <p className="text-gray-600">
          You've completed all {flashCards.totalCards} flash cards about "{flashCards.title}"
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{flashCards.totalCards}</div>
          <div className="text-sm text-gray-500">Cards Completed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{flashCards.language}</div>
          <div className="text-sm text-gray-500">Language</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{flashCards.difficulty}</div>
          <div className="text-sm text-gray-500">Difficulty</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">100%</div>
          <div className="text-sm text-gray-500">Progress</div>
        </div>
      </div>

      <div className="flex gap-4 justify-center">
        <button
          onClick={onPlayAgain}
          className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Play Again
        </button>
        <button
          onClick={onRestart}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Brain className="w-4 h-4" />
          Try Another Set
        </button>
      </div>
    </div>
  );
};

export default FlashCardsFallback; 